import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';
import { getCompanyInfo, getServices } from '../api/api';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [company, setCompany] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyData, servicesData] = await Promise.all([
          getCompanyInfo(),
          getServices()
        ]);
        setCompany(companyData);
        setServices(servicesData);
      } catch (error) {
        console.error('Error fetching footer data:', error);
      }
    };

    fetchData();
  }, []);

  if (!company) {
    return null;
  }

  const navItems = [
    { label: 'Accueil', to: '/' },
    { label: 'Services', to: '/services' },
    { label: 'À propos', to: '/a-propos' },
    { label: 'Avis clients', to: '/avis' },
    { label: 'Contact', to: '/contact' },
  ];

  // Réseaux sociaux : affichés uniquement s'ils sont renseignés dans le panel.
  const socials = [
    { Icon: Facebook, url: company.facebook_url, label: 'Facebook' },
    { Icon: Instagram, url: company.instagram_url, label: 'Instagram' },
    { Icon: Linkedin, url: company.linkedin_url, label: 'LinkedIn' },
  ].filter((s) => s.url);

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <img
                src="/logo-white.png"
                alt={`${company.name} — entreprise de nettoyage à Liège`}
                className="h-10 w-auto"
                width="160"
                height="40"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {company.description}
            </p>
            {/* Social Links (affichés seulement si renseignés) */}
            {socials.length > 0 && (
              <div className="flex space-x-3">
                {socials.map(({ Icon, url, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-800 hover:bg-blue-600 p-2.5 rounded-lg transition-colors duration-300"
                    aria-label={label}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4">Liens rapides</h3>
            <ul className="space-y-3">
              {navItems.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-4">Nos services</h3>
            <ul className="space-y-3">
              {services.slice(0, 6).map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-left"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm text-gray-500 mb-1">Téléphone</div>
                  <a
                    href={`tel:${company.phone}`}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {company.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm text-gray-500 mb-1">Email</div>
                  <a
                    href={`mailto:${company.email}`}
                    className="text-gray-300 hover:text-white transition-colors break-all"
                  >
                    {company.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm text-gray-500 mb-1">Adresse</div>
                  <div className="text-gray-300">
                    {company.address}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} {company.name}. Tous droits réservés.
              <span className="mx-2 text-gray-600">·</span>
              <span>
                Site réalisé par{' '}
                <a
                  href="https://www.mihrem.com"
                  target="_blank"
                  rel="noopener"
                  className="text-gray-300 hover:text-white underline underline-offset-2 transition-colors"
                >
                  Mihrem
                </a>
              </span>
            </div>
            <nav aria-label="Informations légales" className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <Link to="/mentions-legales" className="text-gray-400 hover:text-white transition-colors">
                Mentions légales
              </Link>
              <Link to="/confidentialite" className="text-gray-400 hover:text-white transition-colors">
                Politique de confidentialité
              </Link>
              <Link to="/cgv" className="text-gray-400 hover:text-white transition-colors">
                CGV
              </Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};
